import React, { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { BiGhost } from 'react-icons/bi';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Drawer,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Message, People } from '@mui/icons-material';
import { ChatMessage } from '@par/shared/common/types';
import { WhosOnline } from '../room/types';
import { DrawerButtonWhosOnline } from './DrawerButtonWhosOnline';
import { MessagesDrawer } from './MessagesDrawer';
import { AllChat } from './AllChat';
import { DrawerButtonGhost } from './DrawerButtonGhost';
import { DrawerButtonAllChat } from './DrawerButtonAllChat';
import { Profile } from '@par/shared/types/player';

export const MAX_PLAYERS_ONLINE_COUNT = 50;

export const DrawerButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1A2027',
  color: 'white',
  width: 288,
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
}));

export const ExpandIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxSizing: 'border-box',
  backgroundColor: 'transparent',
  outline: 0,
  border: 0,
  margin: 0,
  cursor: 'pointer',
  userSelect: 'none',
  verticalAlign: 'middle',
  appearance: 'none',
  textDecoration: 'none',
  textAlign: 'center',
  flex: '0 0 auto',
  borderRadius: '50%',
  overflow: 'visible',
  color: 'rgb(255, 255, 255)',
  transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  padding: '5px',
  fontSize: '1.125rem',
  float: 'right',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
}));

export const getWhosOnlineCount = (
  whosOnline: WhosOnline[],
  profileId: string
) =>
  whosOnline?.filter((u) => u.online && !u.isIdle && u.profileId !== profileId)
    .length;

type Props = {
  allChat: ChatMessage[];
  incomingGhostChallenges: number;
  openAllChat: boolean;
  playersOnlineCount: number;
  profile: Profile;
  socket: Socket;
  whosOnline: WhosOnline[];
  handleGhostChallenge: (player: WhosOnline) => void;
  handleGhostChallengesView: () => void;
  setOpenAllChat: (open: boolean) => void;
  unreadAllChat: boolean;
};

export const BottomNav = ({
  allChat,
  incomingGhostChallenges,
  openAllChat,
  profile,
  playersOnlineCount,
  socket,
  whosOnline,
  handleGhostChallenge,
  handleGhostChallengesView,
  setOpenAllChat,
  unreadAllChat,
}: Props) => {
  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));
  const BOX_SHADOW =
    '0px 0px 4px -1px #cda68c, 0px 0px 5px 0px #cda68c, 0px 0px 10px 0px #cda68c';
  const HEIGHT_FROM_BOTTOM = 'calc(100vh - 192px)';

  const [openMessagesDrawer, setOpenMessagesDrawer] = useState<boolean>();

  useEffect(() => {
    if (openMessagesDrawer && xsBreakpoint) {
      setOpenAllChat(false);
    }
  }, [openMessagesDrawer]);

  useEffect(() => {
    if (openAllChat && xsBreakpoint) {
      setOpenMessagesDrawer(false);
    }
  }, [openAllChat]);

  const mobileBottomNav = (
    <BottomNavigation
      sx={{
        position: 'fixed',
        bottom: 0,
        width: '100%',
        backgroundColor: '#1A2027',
        borderTop: `1px solid ${theme.palette.primary.dark}`,
      }}
      showLabels
    >
      <BottomNavigationAction
        label={
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Typography color="success.main" fontSize={12}>
              {playersOnlineCount > MAX_PLAYERS_ONLINE_COUNT
                ? playersOnlineCount
                : getWhosOnlineCount(whosOnline, profile.id)}
            </Typography>{' '}
            <Typography
              textTransform={'capitalize'}
              whiteSpace={'nowrap'}
              fontSize={12}
            >
              Player{playersOnlineCount !== 1 ? <span>s</span> : null} Online
            </Typography>
          </Stack>
        }
        icon={<People />}
        onClick={(event) => setOpenMessagesDrawer(!openMessagesDrawer)}
      />
      <BottomNavigationAction
        icon={<BiGhost size={32} />}
        onClick={handleGhostChallengesView}
      />
      <BottomNavigationAction
        label="All Chat"
        icon={<Message />}
        onClick={() => setOpenAllChat(!openAllChat)}
      />
    </BottomNavigation>
  );

  return (
    <Box
      position={'fixed'}
      bottom={0}
      width={'100vw'}
      height={48}
      zIndex={1200}
    >
      {!xsBreakpoint ? (
        <>
          {/* Whos Online */}
          <DrawerButtonWhosOnline
            openMessagesDrawer={openMessagesDrawer}
            profile={profile}
            setOpenMessagesDrawer={setOpenMessagesDrawer}
            onlinePlayerCount={playersOnlineCount}
            whosOnline={whosOnline}
          />

          {/* Ghost */}
          <DrawerButtonGhost
            handleGhostChallengesView={handleGhostChallengesView}
            incomingGhostChallenges={incomingGhostChallenges}
          />

          {/* All Chat */}
          <DrawerButtonAllChat
            openAllChat={openAllChat}
            setOpenAllChat={setOpenAllChat}
            unreadAllChat={unreadAllChat}
          />
        </>
      ) : (
        mobileBottomNav
      )}

      {/* Who's Online */}
      <Drawer
        anchor={'bottom'}
        open={openMessagesDrawer}
        hideBackdrop
        variant="persistent"
        onClose={() => setOpenMessagesDrawer(!openMessagesDrawer)}
        PaperProps={{
          sx: {
            boxShadow: BOX_SHADOW,
            border: 'none',
            width: 288,
            bottom: openMessagesDrawer ? (xsBreakpoint ? 56 : 0) : 40,
            height: HEIGHT_FROM_BOTTOM,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          },
        }}
      >
        <MessagesDrawer
          onlinePlayerCount={playersOnlineCount}
          profile={profile}
          whosOnline={whosOnline}
          handleGhostChallenge={handleGhostChallenge}
          openDrawer={openMessagesDrawer}
          setOpenDrawer={setOpenMessagesDrawer}
        />
      </Drawer>

      {/* All Chat */}
      <Drawer
        anchor={'bottom'}
        open={openAllChat}
        hideBackdrop
        variant="persistent"
        onClose={() => setOpenAllChat(!openAllChat)}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            left: { xs: 0, sm: `calc(50% - 298px)` },
          },
        }}
        PaperProps={{
          sx: {
            boxShadow: BOX_SHADOW,
            border: 'none',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            width: xsBreakpoint ? '100vw' : 596,
            bottom: openAllChat ? (xsBreakpoint ? 56 : 0) : 40,
            height: xsBreakpoint ? 'calc(100% - 54px)' : 400,
          },
        }}
      >
        <AllChat
          allChat={allChat}
          handleMinimizeAllChat={() => setOpenAllChat(!openAllChat)}
          open={openAllChat}
          socket={socket}
          username={profile.username}
        ></AllChat>
      </Drawer>
    </Box>
  );
};
